import { useMemo, useState } from 'react';

export interface QuizOption {
  id: string;
  text: string;
}

interface QuizQuestionProps {
  question: string;
  options: QuizOption[];
  onSubmit: (selectedOptionIds: string[]) => void | Promise<void>;
  disabled?: boolean;
}

function QuizQuestion({ question, options, onSubmit, disabled = false }: QuizQuestionProps) {
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);

  const selectionSummary = useMemo(() => {
    return selectedOptionIds.length === 0 ? 'No option selected' : selectedOptionIds.join(', ');
  }, [selectedOptionIds]);

  const toggleOption = (optionId: string) => {
    setSelectedOptionIds((current) => {
      if (current.includes(optionId)) {
        return current.filter((id) => id !== optionId);
      }
      return [...current, optionId];
    });
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (disabled || selectedOptionIds.length === 0) {
          return;
        }
        void onSubmit(selectedOptionIds);
      }}
      style={{ display: 'grid', gap: '1rem' }}
    >
      <div>
        <h2>{question}</h2>
        <p aria-live="polite" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
          Selected: {selectionSummary}
        </p>
      </div>

      <fieldset style={{ border: '1px solid var(--color-border-strong)', borderRadius: '0.75rem', padding: '1rem' }}>
        <legend style={{ padding: '0 0.5rem' }}>Options</legend>
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {options.map((option) => (
            <label
              key={option.id}
              htmlFor={`option-${option.id}`}
              style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                id={`option-${option.id}`}
                type="checkbox"
                aria-label={`Option ${option.id}`}
                checked={selectedOptionIds.includes(option.id)}
                onChange={() => toggleOption(option.id)}
                disabled={disabled}
              />
              <span>{option.text}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <button type="submit" disabled={disabled || selectedOptionIds.length === 0}>
        Submit answer
      </button>
    </form>
  );
}

export default QuizQuestion;
